/**
 * API 金鑰的**權限範圍（scope）勾選邏輯**（批147）——全部是純函式，元件只負責畫。
 *
 * scope 的形狀是 `<類>:read`／`<類>:write`，目錄（哪些類、各有哪些動作、涵蓋幾支端點）
 * 由後端 `GET /api/admin/api-keys/scopes` 給，**前端不寫死任何一類**——後端加一類，
 * 勾選格自動多一列。
 *
 * ## 寫→讀連動（主人規格）
 *
 * - 勾「寫」自動勾「讀」：能改卻不能讀的金鑰沒有實際用途（改完連結果都查不了），
 *   而且後端是**逐端點**比對 scope，不會因為有寫就放行讀。
 * - 取消「讀」時一併取消「寫」：與上一條對稱，免得留下「只有寫」的半套組合。
 * - 只有 `read` 的類（員工帳號、稽核、SQL…）沒有寫可連動，照常單勾。
 *
 * ## 順序
 *
 * 一律回傳**目錄順序**（類別列序 → 讀在寫前）。送給後端的陣列因此是穩定的，
 * 編輯時比對「有沒有改」也不會被順序干擾（比對另外用集合，見 `sameScopeSet`）。
 */
import type { ApiKeyScopeCatalog, ApiKeyScopeCategory } from '@/api/ls'

export type ScopeAction = 'read' | 'write'

/** `customers` ＋ `read` → `customers:read`。 */
export function scopeOf(category: string, action: ScopeAction): string {
  return `${category}:${action}`
}

/** `customers:write` → `{ category: 'customers', action: 'write' }`（不認得的動作原樣帶回）。 */
export function splitScope(scope: string): { category: string; action: string } {
  const i = scope.indexOf(':')
  if (i < 0) return { category: scope, action: '' }
  return { category: scope.slice(0, i), action: scope.slice(i + 1) }
}

/** 目錄裡全部合法的 scope（目錄順序）。 */
export function allScopes(catalog: ApiKeyScopeCatalog): string[] {
  return catalog.categories.flatMap((c) => c.actions.map((a) => a.scope))
}

/** 這一類有沒有某個動作（只有 read 的類問 write 會得到 false）。 */
export function hasAction(category: ApiKeyScopeCategory, action: ScopeAction): boolean {
  return category.actions.some((a) => a.action === action)
}

/**
 * 送出前整理：去重、丟掉目錄裡沒有的值、補上「寫」隱含的「讀」、照目錄排序。
 *
 * ★ 丟掉未知值而不是原樣送出：後端遇到未知 scope 會整包 422，而未知值只可能來自
 *   「後端已經把那一類拿掉、這把舊金鑰還帶著」——送出去只會讓整次修改失敗。
 */
export function normalizeScopes(selected: readonly string[], catalog: ApiKeyScopeCatalog): string[] {
  const set = new Set(selected)
  for (const c of catalog.categories) {
    // 只有「目錄裡真的有寫」的類才連動：只有讀的類若混進一個 `audit:write`（未知值），
    // 它該被丟掉，而不是反過來替人勾上 `audit:read`。
    if (hasAction(c, 'write') && hasAction(c, 'read') && set.has(scopeOf(c.key, 'write'))) {
      set.add(scopeOf(c.key, 'read'))
    }
  }
  return allScopes(catalog).filter((s) => set.has(s))
}

/**
 * 勾／取消一格（含寫→讀連動，見檔頭）。回傳新陣列（目錄順序），不改動傳入的陣列。
 *
 * 只動被點的那一類：其他類就算本來是「只有寫」的舊組合也原樣保留——那是
 * `normalizeScopes` 在送出時才處理的事，勾一格不該順手改到別列。
 */
export function setScope(
  selected: readonly string[],
  scope: string,
  on: boolean,
  catalog: ApiKeyScopeCatalog,
): string[] {
  const known = new Set(allScopes(catalog))
  if (!known.has(scope)) return orderByCatalog(selected, catalog)
  const set = new Set(selected)
  const { category, action } = splitScope(scope)
  const read = scopeOf(category, 'read')
  const write = scopeOf(category, 'write')
  if (on) {
    set.add(scope)
    if (action === 'write' && known.has(read)) set.add(read)
  } else {
    set.delete(scope)
    if (action === 'read') set.delete(write)
  }
  return orderByCatalog([...set], catalog)
}

/** 〔全選讀取〕：每一類都勾上讀（已勾的寫保留）。 */
export function selectAllRead(selected: readonly string[], catalog: ApiKeyScopeCatalog): string[] {
  const set = new Set(selected)
  for (const c of catalog.categories) if (hasAction(c, 'read')) set.add(scopeOf(c.key, 'read'))
  return orderByCatalog([...set], catalog)
}

/** 〔全選〕：目錄裡每一格。 */
export function selectAll(catalog: ApiKeyScopeCatalog): string[] {
  return allScopes(catalog)
}

/** 〔清除〕。 */
export function clearScopes(): string[] {
  return []
}

/** 這一類目前勾了什麼（畫勾選格用）。 */
export function categoryState(
  selected: readonly string[],
  category: ApiKeyScopeCategory,
): { read: boolean; write: boolean } {
  return {
    read: selected.includes(scopeOf(category.key, 'read')),
    write: selected.includes(scopeOf(category.key, 'write')),
  }
}

/** 兩組 scope 是不是同一個集合（不看順序、不看重複）。編輯時判斷「有沒有改」。 */
export function sameScopeSet(a: readonly string[], b: readonly string[]): boolean {
  const sa = new Set(a)
  const sb = new Set(b)
  if (sa.size !== sb.size) return false
  for (const s of sa) if (!sb.has(s)) return false
  return true
}

/** 依目錄排序並去重；目錄裡沒有的值排在最後（保持原相對順序）。 */
function orderByCatalog(selected: readonly string[], catalog: ApiKeyScopeCatalog): string[] {
  const set = new Set(selected)
  const known = allScopes(catalog)
  const knownSet = new Set(known)
  return [...known.filter((s) => set.has(s)), ...[...set].filter((s) => !knownSet.has(s))]
}

// ---------------------------------------------------------------------------
// 清單上的 scope 膠囊
// ---------------------------------------------------------------------------

export interface ScopeChip {
  /** v-for 的 key（類別鍵；不認得的 scope 用它本身）。 */
  key: string
  /** 膠囊上的字：`客戶・讀寫`／`稽核紀錄・讀`。 */
  text: string
  /** 滑過去看的完整說明（類別全名＋動作）。 */
  title: string
  /** 含寫入（膠囊用較醒目的樣式）。 */
  write: boolean
}

/**
 * 類別的**短名**（膠囊用）：拿掉結尾的全形括號說明。
 * `系統設定（樣板／代管種類／指標）` → `系統設定`、`授權（主證）` → `授權`。
 */
export function shortCategoryLabel(label: string): string {
  const s = label.replace(/（[^（）]*）$/, '').trim()
  return s || label
}

/**
 * 金鑰的 scope → 一類一顆膠囊（目錄順序）。同一類讀寫都有就併成「讀寫」。
 * 目錄裡沒有的值（後端已移除的類）各自一顆、原字串顯示——不吞掉，免得看起來權限比實際少。
 */
export function scopeChips(scopes: readonly string[], catalog: ApiKeyScopeCatalog): ScopeChip[] {
  const set = new Set(scopes)
  const out: ScopeChip[] = []
  const used = new Set<string>()
  for (const c of catalog.categories) {
    const r = set.has(scopeOf(c.key, 'read'))
    const w = set.has(scopeOf(c.key, 'write'))
    if (!r && !w) continue
    used.add(scopeOf(c.key, 'read'))
    used.add(scopeOf(c.key, 'write'))
    const act = r && w ? '讀寫' : w ? '寫' : '讀'
    const actFull = r && w ? '讀取＋寫入' : w ? '寫入' : '讀取'
    out.push({
      key: c.key,
      text: `${shortCategoryLabel(c.label)}・${act}`,
      title: `${c.label}：${actFull}`,
      write: w,
    })
  }
  for (const s of scopes) {
    if (used.has(s)) continue
    used.add(s)
    out.push({ key: s, text: s, title: `目錄裡沒有的權限範圍：${s}`, write: splitScope(s).action === 'write' })
  }
  return out
}

/**
 * 膠囊收合：超過 `max` 顆時只顯示前 `max` 顆，其餘收成「＋N」。
 * 剛好多一顆時不收（「＋1」佔的位置跟那一顆差不多，收起來只是多一個要滑過去看的東西）。
 */
export function splitChips<T>(chips: readonly T[], max: number): { shown: T[]; hidden: T[] } {
  if (chips.length <= max + 1) return { shown: [...chips], hidden: [] }
  return { shown: chips.slice(0, max), hidden: chips.slice(max) }
}
